/**
 * Treatment tracks: what the modality actually changes.
 *
 * Someone on chemotherapy takes their temperature every morning and watches for a fever that means
 * hospital tonight. Someone on an immunotherapy watches their bowels and their breathing, because
 * the drug can turn on the gut or the lungs. Someone on an oral therapy has the whole treatment in
 * their own hands and the job is taking it exactly as prescribed. The track is what the app asks
 * for, shows, and coaches on.
 *
 * The track is a DERIVATION, never state: `trackFor(treatments)` is read at view time and is never
 * consulted by `applyEvent`. So an oncologist correcting a plan changes today's questions and
 * nothing about the past — no migration, no replay drift.
 */

import type { CareGoal, MetricKind, ModalityCode, SymptomCode, Treatment } from './types.js';

/** A one-tap thing to do. The id is the contract; the host decides what it looks like. */
export type TrackActionId = 'log-temperature' | 'symptoms' | 'meds' | 'log-intake' | 'log-weight' | 'log-steps' | 'coach' | 'call-clinic';

export interface TrackAction {
  id: TrackActionId;
  label: string;
}

export interface CareTrack {
  id: ModalityCode;
  label: string;
  /** The one-line job of this track, in the patient's words. */
  focus: string;
  /** Which metrics this patient is asked for and shown, in order. */
  metrics: MetricKind[];
  /** Which symptoms the check offers, most decision-relevant first. */
  symptoms: SymptomCode[];
  quickActions: TrackAction[];
  /** What an oncologist enrolling this patient gets pre-filled. */
  defaultGoals: CareGoal[];
  /** Whether a fever on this track is the febrile-neutropenia emergency. */
  feverIsEmergency: boolean;
  /** Whether this track counts in cycles at all. Survivorship does not. */
  hasCycles: boolean;
  /** A paragraph for the coach prompt: what matters for this patient and what to watch. */
  coachFocus: string;
  /** The daily check-in line. One question, answerable in two taps. */
  checkInPrompt: string;
}

const GOAL_ADHERENCE: CareGoal = { kind: 'adherence', percent: 95 };
const GOAL_SYMPTOMS: CareGoal = { kind: 'symptom-check', daysPerWeek: 7 };

export const TRACKS: Readonly<Record<ModalityCode, CareTrack>> = {
  chemotherapy: {
    id: 'chemotherapy',
    label: 'Chemotherapy',
    focus: 'The fever watch, and getting through the cycle',
    metrics: ['intake-ml', 'weight-lbs', 'heart-rate', 'sleep-hours'],
    symptoms: ['nausea', 'vomiting', 'diarrhea', 'mouth-sores', 'fatigue', 'appetite-loss', 'numbness', 'bleeding', 'confusion', 'breathlessness'],
    quickActions: [
      { id: 'log-temperature', label: 'Take temperature' },
      { id: 'symptoms', label: 'How I feel' },
      { id: 'meds', label: 'Medications' },
      { id: 'coach', label: 'Ask coach' },
    ],
    defaultGoals: [{ kind: 'daily-temperature', daysPerWeek: 7 }, GOAL_SYMPTOMS, { kind: 'intake', mlPerDay: 2000 }, GOAL_ADHERENCE],
    feverIsEmergency: true,
    hasCycles: true,
    coachFocus:
      'On chemotherapy. The single most important thing this patient does is take their temperature, because a fever of 38.0 °C or more during treatment is a medical emergency — febrile neutropenia — and needs the oncology line or the emergency department the same hour, not a message here. Days 7 to 14 after a dose are when the white count is lowest and the risk is highest. Beyond that: keeping fluids in, mouth care, and not dismissing a symptom as "just the chemo".',
    checkInPrompt: 'Take your temperature and log it. It is the one number that tells us you are safe today.',
  },
  immunotherapy: {
    id: 'immunotherapy',
    label: 'Immunotherapy',
    focus: 'Catching an immune reaction early',
    metrics: ['bowel-movements', 'weight-lbs', 'heart-rate', 'sleep-hours'],
    symptoms: ['diarrhea', 'breathlessness', 'cough', 'rash', 'fatigue', 'nausea', 'pain', 'confusion', 'appetite-loss'],
    quickActions: [
      { id: 'symptoms', label: 'How I feel' },
      { id: 'log-temperature', label: 'Take temperature' },
      { id: 'meds', label: 'Medications' },
      { id: 'coach', label: 'Ask coach' },
    ],
    defaultGoals: [GOAL_SYMPTOMS, { kind: 'daily-temperature', daysPerWeek: 4 }, GOAL_ADHERENCE],
    feverIsEmergency: true,
    hasCycles: true,
    coachFocus:
      'On immunotherapy. These drugs work by taking the brakes off the immune system, so the side effects that matter are the immune system attacking healthy organs: colitis (diarrhoea, more stools than usual, blood), pneumonitis (new cough, breathlessness), hepatitis, thyroid and adrenal problems. These can escalate over days and are treated with steroids — early reporting genuinely changes the outcome, so never counsel waiting to see. Diarrhoea on immunotherapy is not food poisoning until the team says so.',
    checkInPrompt: 'How many times have your bowels opened today, and is that more than usual for you?',
  },
  'oral-therapy': {
    id: 'oral-therapy',
    label: 'Oral therapy',
    focus: 'Taking it exactly as prescribed',
    metrics: ['weight-lbs', 'intake-ml', 'exertion-minutes', 'sleep-hours'],
    symptoms: ['skin-breakdown', 'diarrhea', 'nausea', 'mouth-sores', 'fatigue', 'rash', 'appetite-loss', 'pain'],
    quickActions: [
      { id: 'meds', label: 'Medications' },
      { id: 'symptoms', label: 'How I feel' },
      { id: 'log-temperature', label: 'Take temperature' },
      { id: 'coach', label: 'Ask coach' },
    ],
    defaultGoals: [GOAL_ADHERENCE, GOAL_SYMPTOMS, { kind: 'weekly-weight', daysPerWeek: 2 }],
    feverIsEmergency: true,
    hasCycles: true,
    coachFocus:
      'On an oral anti-cancer therapy, taken at home. The whole treatment is in this patient\'s hands, which makes two things matter above all: taking it exactly as prescribed, and knowing when to stop. Never tell them to skip, double, or make up a missed dose — that is always a question for the oncology team, and the answer depends on the drug. Hand-foot syndrome (sore, peeling palms and soles), mouth ulcers and diarrhoea are the side effects that most often mean a dose needs changing, and are worth reporting before they become severe.',
    checkInPrompt: 'Tick off today\'s doses, and tell me if anything has changed with your hands, mouth or bowels.',
  },
  radiation: {
    id: 'radiation',
    label: 'Radiotherapy',
    focus: 'Skin, swallowing and keeping your strength up',
    metrics: ['weight-lbs', 'intake-ml', 'sleep-hours', 'exertion-minutes'],
    symptoms: ['skin-breakdown', 'swallowing', 'fatigue', 'pain', 'appetite-loss', 'nausea', 'cough'],
    quickActions: [
      { id: 'symptoms', label: 'How I feel' },
      { id: 'log-weight', label: 'Log weight' },
      { id: 'log-intake', label: 'Log fluids' },
      { id: 'coach', label: 'Ask coach' },
    ],
    defaultGoals: [GOAL_SYMPTOMS, { kind: 'weekly-weight', daysPerWeek: 2 }, { kind: 'intake', mlPerDay: 2000 }],
    feverIsEmergency: false,
    hasCycles: false,
    coachFocus:
      'Having radiotherapy. The side effects are local to where the beam goes and build up over the weeks rather than arriving at once: skin that reddens, dries and can break down; a sore throat and painful swallowing when the chest or neck is treated; and a fatigue that is real and worst towards the end. Weight falling because eating hurts is the thing that derails treatment, so intake and weight are worth watching closely. Skin that has broken or is weeping needs the team to see it.',
    checkInPrompt: 'How is the skin in the treated area today, and how is eating and drinking going?',
  },
  survivorship: {
    id: 'survivorship',
    label: 'After treatment',
    focus: 'Getting strength back, and staying watchful',
    metrics: ['exertion-minutes', 'weight-lbs', 'sleep-hours', 'heart-rate'],
    symptoms: ['fatigue', 'pain', 'numbness', 'appetite-loss', 'breathlessness', 'cough', 'bleeding'],
    quickActions: [
      { id: 'log-steps', label: 'Log activity' },
      { id: 'symptoms', label: 'How I feel' },
      { id: 'log-weight', label: 'Log weight' },
      { id: 'coach', label: 'Ask coach' },
    ],
    defaultGoals: [{ kind: 'steps', target: 6000 }, { kind: 'symptom-check', daysPerWeek: 3 }, { kind: 'weekly-weight', daysPerWeek: 1 }],
    feverIsEmergency: false,
    hasCycles: false,
    coachFocus:
      'Finished active treatment. The work now is rebuilding: activity that increases gradually, sleep, and eating enough. Fatigue and neuropathy can take many months to improve and that is normal to say. What is not normal, and should always go to the team rather than be reassured away, is a symptom that returns and persists — new pain that does not settle, unexplained weight loss, bleeding, or a lump. Follow-up appointments are the other half of this, and are worth protecting.',
    checkInPrompt: 'How much did you move yesterday, and is anything new or persistent worth telling your team?',
  },
};

/**
 * Which track a patient is on when they are on more than one treatment. Total and fixed: the
 * modality whose daily job is most time-critical wins, and a patient with nothing recorded is in
 * survivorship, which asks the least and claims the least.
 */
export const TRACK_PRIORITY: readonly ModalityCode[] = ['chemotherapy', 'immunotherapy', 'oral-therapy', 'radiation', 'survivorship'];

export function trackFor(treatments: readonly Treatment[]): CareTrack {
  for (const code of TRACK_PRIORITY) {
    if (treatments.some((t) => t.code === code)) return TRACKS[code];
  }
  return TRACKS.survivorship;
}

/** Whether this patient is on active treatment, which is what makes a fever an emergency. */
export function onTreatment(treatments: readonly Treatment[]): boolean {
  return trackFor(treatments).feverIsEmergency;
}

/** How each metric is named and rendered. One definition, so the tile and the coach agree. */
export const METRIC_META: Readonly<Record<MetricKind, { label: string; unit: string; decimals: number }>> = {
  'weight-lbs': { label: 'Weight', unit: 'lbs', decimals: 1 },
  'heart-rate': { label: 'Resting pulse', unit: 'bpm', decimals: 0 },
  'sleep-hours': { label: 'Sleep', unit: 'hrs', decimals: 1 },
  'exertion-minutes': { label: 'Active minutes', unit: 'min', decimals: 0 },
  'intake-ml': { label: 'Fluids', unit: 'ml', decimals: 0 },
  'bowel-movements': { label: 'Bowel movements', unit: 'today', decimals: 0 },
};

/** The words behind each grade. The patient is asked in these, never in numbers. */
export const GRADE_LABEL: readonly string[] = ['None', 'Mild', 'Moderate', 'Severe', 'Unable to do anything'];

export const SYMPTOM_LABEL: Readonly<Record<SymptomCode, string>> = {
  none: 'Nothing today',
  nausea: 'Feeling sick',
  vomiting: 'Being sick',
  diarrhea: 'Diarrhoea',
  constipation: 'Constipation',
  'mouth-sores': 'Sore mouth',
  'appetite-loss': 'Not eating',
  swallowing: 'Painful swallowing',
  fatigue: 'Exhaustion',
  pain: 'Pain',
  numbness: 'Numbness or tingling',
  rash: 'Rash',
  'skin-breakdown': 'Sore or broken skin',
  cough: 'Cough',
  breathlessness: 'Breathlessness',
  bleeding: 'Bleeding or bruising',
  confusion: 'Confusion',
};
